import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Divider,
} from "@mui/material";
import { XMarkIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useSnackbar } from "../hooks/SnackbarProvider";
import { addCommentsForSingleProduct } from "../../../../config/Services";
import { colorConstant } from "../../../../styles/colorConstant/colorConstant";
import PosCustomIcon from "../../../../customComponents/PosCustomIcon/PosCustomIcon";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 450 },
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: "0px 10px 25px rgba(0,0,0,0.1)",
  overflow: "hidden",
};

const ProductCommentModal = ({
  open,
  handleClose,
  product,
  cartId,
  uidx,
  userName,
  onSaveSuccess,
}) => {
  const [comment, setComment] = useState(product?.comments || "");
  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  const handleSave = async () => {
    if (loading) return;
    setLoading(true);
    const payload = {
      cartId: cartId,
      cartProductId: product?.cartProductId,
      comments: comment.trim(),
      updatedById: uidx,
      updatedByName: userName,
    };

    try {
      const res = await addCommentsForSingleProduct(payload);
      if (res?.data?.data?.es === 0 || res?.data?.data?.message === "SUCCESS") {
        showSnackbar("Instructions updated", "success");
        onSaveSuccess(res?.data?.data?.data?.cartProducts);
        handleClose();
      } else {
        showSnackbar(res?.data?.message || "Failed to save instructions", "error");
      }
    } catch (error) {
      showSnackbar("Error saving instructions", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="product-comment-title"
      closeAfterTransition
    >
      <Box sx={style}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            bgcolor: "#f8faff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: "#eef4ff",
                p: 1,
                borderRadius: "8px",
                display: "flex",
              }}
            >
              <ChatBubbleLeftRightIcon style={{ width: 20, color: "#0860E6" }} />
            </Box>
            <Typography
              id="product-comment-title"
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: colorConstant.surfaceBlack }}
            >
              Cooking Instructions
            </Typography>
          </Box>

          <PosCustomIcon
            icon={<XCircleIcon />}
            color="var(--surface-black)"
            height={"22px"}
            width={"22px"}
            onClickFunction={handleClose}
            pointer={true}
          />
        </Box>

        <Divider />

        {/* Body */}
        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{ mb: 1, color: colorConstant?.surfaceBlack, fontWeight: 500 }}
          >
            Item: <span style={{ color: "#000", fontWeight: 700 }}>{product?.productName}</span>
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="e.g. Less spicy, make it well done, allergic to peanuts..."
            value={comment}
            autoFocus
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 200 }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: "0.9rem",
                bgcolor: "#fff",
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "right",
              mt: 0.5,
              color: colorConstant?.surfaceBlack,
            }}
          >
            {comment.length}/200 characters
          </Typography>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 2,
            bgcolor: "#f8faff",
          }}
        >
          <PosCustomButton
            title="Cancel"
            variant="blue-s"
            p={"12px 16px"}
            radius={"6px"}
            width="100%"
            onClickFunction={handleClose}
          />

          <PosCustomButton
            title="Add"
            variant="blue-d"
            p={"12px 16px"}
            radius={"6px"}
            width="100%"
            disabled={loading}
            onClickFunction={handleSave}
            loading={loading}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductCommentModal;
